import React from 'react'

import { Modal, useModal } from '@gousto-internal/citrus-react'
import actions from 'actions'
import { useDispatch, useSelector } from 'react-redux'
import { browserHistory } from 'react-router'

import { BasketChangesContent } from '@features/basket'
import type { Recipe } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'
import type { BasketRecipe } from 'hooks/basket/useBasketRecipes'
import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useMaxRecipesForPortion } from 'hooks/menu-boxes/useMaxRecipesForPortion'
import { useBasket, useNumRecipesOverBasketLimit } from 'routes/Menu/domains/basket'
import { useScrollableBasket } from 'routes/Menu/hooks/useScrollableBasket'
import { createGetActionTypeIsPending } from 'selectors/status'

export const BasketChangesModal = () => {
  const { closeCurrentModal } = useModal()
  const dispatch = useDispatch()
  const { removeRecipe } = useBasket()
  const { numPortions } = useNumPortions()
  const maxRecipesNum = useMaxRecipesForPortion(numPortions)
  const numRecipesOverLimit = useNumRecipesOverBasketLimit()
  const isScrollableBasketEnabled = useScrollableBasket()

  const isLoading = useSelector(createGetActionTypeIsPending(actionTypes.MENU_FETCH_DATA))
  const isOverRecipeLimit = !isScrollableBasketEnabled && numRecipesOverLimit > 0

  const { basketRecipes } = useBasketRecipes()
  const selectedRecipes: Recipe[] = basketRecipes
    .map((basketItem: BasketRecipe) => basketItem.recipe)
    .filter((recipe): recipe is Recipe => Boolean(recipe))

  const isAnyRecipeNotAvailable = selectedRecipes.some((recipe) => !recipe.isAvailable)
  const isModalOpen = !isLoading && (isOverRecipeLimit || isAnyRecipeNotAvailable)

  const goBack = () => {
    dispatch(actions.basketEditModalClosed(selectedRecipes, maxRecipesNum, numPortions))
    closeCurrentModal()
    browserHistory.goBack()
  }

  const submitRemovedRecipes = (recipeUUIDsToRemove: string[]) => {
    dispatch(
      actions.basketEditChangesConfirmed(
        selectedRecipes,
        recipeUUIDsToRemove,
        maxRecipesNum,
        numPortions,
      ),
    )
    closeCurrentModal()

    recipeUUIDsToRemove.forEach((uuid: string) => {
      const coreId = basketRecipes.find((basketItem) => basketItem.recipe?.id === uuid)?.coreId

      if (coreId) {
        removeRecipe(coreId, 'basket_changes_modal')
      }
    })
  }

  const handleAfterOpen = () => {
    dispatch(actions.basketEditModalOpen(selectedRecipes, maxRecipesNum, numPortions))
  }

  return (
    <Modal
      shouldCloseOnOverlayClick={false}
      isOpen={isModalOpen}
      name="basket-changes"
      onAfterOpen={handleAfterOpen}
      preventScroll
    >
      <BasketChangesContent
        maxNumberOfRecipes={maxRecipesNum}
        numberOfPortionsPerBox={numPortions}
        recipes={selectedRecipes}
        submitRemovedRecipes={submitRemovedRecipes}
        goBack={goBack}
        isScrollableBasketEnabled={isScrollableBasketEnabled}
      />
    </Modal>
  )
}
